import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import AnalysisTabs from "@/pages/detail/components/AnalysisTabs";
import ShareRow from "@/pages/detail/components/ShareRow";
import { getMovieById, getRelatedMovies } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const infoLabels = ["导演", "主演", "类型", "地区", "首播年份", "集数 / 时长"] as const;

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const related = movie ? getRelatedMovies(movie, 3) : [];

  usePageMeta({
    title: movie
      ? `${movie.title} - 剧情解析与推荐 - “奸情”影视内容站`
      : "剧集不存在 - “奸情”影视内容站",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）：${movie.summary.slice(0, 60)}…… 含分章节剧情解析、角色关系与相关资讯摘要。`
      : "你访问的剧集不存在或已被移除，可以返回分类页继续浏览其他影视剧。",
    keywords: movie ? `${movie.title},剧情解析,影视推荐,${movie.genres.join(",")}` : "影视剧,剧情解析",
  });

  if (!movie) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 pb-20 pt-32">
          <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-2xl text-foreground-400">
              <i className="ri-file-search-line" aria-hidden="true" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">这部影视剧暂时找不到</h1>
            <p className="mt-2 text-sm leading-6 text-foreground-500">
              链接可能已失效，或者剧集信息已被更新。你可以回到分类页重新挑选。
            </p>
            <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-900 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800"
              >
                返回影视剧分类
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700"
              >
                搜索剧名
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const infoValues = [
    movie.director,
    movie.cast.join("、"),
    movie.genres.join(" / "),
    movie.region,
    `${movie.year}`,
    `${movie.episodes} 集 · ${movie.duration}`,
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="relative h-[420px] w-full md:h-[500px]">
            <img
              src={movie.backdrop}
              alt={`${movie.title}剧照大图，${movie.year}年${movie.region}${movie.genres.join("、")}剧`}
              title={`${movie.title} - 剧照与剧情解析`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/55 to-primary-950/45" />
            <div className="absolute inset-x-0 bottom-0 px-4 pb-32 md:px-8 md:pb-40 lg:px-12 lg:pb-44">
              <div className="w-full">
                <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-background-300">
                  <Link to="/" className="transition-colors hover:text-background-50">
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  <Link to="/category" className="transition-colors hover:text-background-50">
                    影视剧分类
                  </Link>
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  <Link
                    to={`/category?genre=${encodeURIComponent(movie.genres[0])}`}
                    className="transition-colors hover:text-background-50"
                  >
                    {movie.genres[0]}
                  </Link>
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  <span className="text-background-100">{movie.title}</span>
                </nav>
                <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-5xl">
                  {movie.title}
                </h1>
                <p className="mt-2 font-label text-xs uppercase tracking-[0.24em] text-background-300 md:text-sm">
                  {movie.enTitle} · {movie.year} · {movie.region}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-200 md:text-sm">
                  <span className="flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 font-label font-bold text-primary-950">
                    <i className="ri-star-fill text-xs" aria-hidden="true" />
                    {movie.rating.toFixed(1)}
                  </span>
                  <span>{movie.genres.join(" / ")}</span>
                  <span className="h-3 w-px bg-background-50/25" />
                  <span>{movie.episodes} 集</span>
                  <span className="h-3 w-px bg-background-50/25" />
                  <span>{movie.duration}</span>
                  <span className="h-3 w-px bg-background-50/25" />
                  <span>热度 {movie.heat.toLocaleString("zh-CN")}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 pb-16 md:px-8 md:pb-20 lg:px-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr] lg:gap-10">
            <aside className="relative z-10 -mt-24 lg:-mt-32">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <img
                  src={movie.poster}
                  alt={`${movie.title}官方海报，${movie.genres.join("、")}题材`}
                  title={`${movie.title} - 剧情解析与推荐`}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950">基本信息</h2>
                <dl className="mt-3 flex flex-col gap-2.5">
                  {infoLabels.map((label, index) => (
                    <div key={label} className="flex gap-3 text-xs leading-5">
                      <dt className="w-16 shrink-0 text-foreground-400">{label}</dt>
                      <dd className="text-foreground-700">{infoValues[index]}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <div className="mt-4">
                <ShareRow title={movie.title} />
              </div>
            </aside>

            <div className="min-w-0 pt-6 lg:pt-8">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: "影视剧分类", to: "/category" },
                  { label: movie.genres[0], to: `/category?genre=${encodeURIComponent(movie.genres[0])}` },
                  { label: movie.title },
                ]}
              />

              <p className="mt-5 font-heading text-lg font-medium leading-8 text-primary-800 md:text-xl">
                “{movie.tagline}”
              </p>

              <div className="mt-6">
                <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  剧情简介
                </h2>
                <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-[15px]">{movie.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-accent-200 bg-accent-50 px-3 py-1 text-xs text-accent-800">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    剧情解析
                  </h2>
                  <p className="text-xs text-foreground-500">共 {movie.analysis.length} 个章节 · 点击切换阅读</p>
                </div>
                <div className="mt-4">
                  <AnalysisTabs chapters={movie.analysis} />
                </div>
                <figure className="mt-4 overflow-hidden rounded-lg border border-background-200">
                  <img
                    src={movie.backdrop}
                    alt={`${movie.title}剧情画面，解析配图`}
                    title={`${movie.title} - 剧情细节解析配图`}
                    className="w-full aspect-[16/9] object-cover object-top"
                  />
                  <figcaption className="bg-background-100 px-4 py-2 text-[11px] text-foreground-500">
                    图 · 《{movie.title}》剧照，用于剧情解析配图展示
                  </figcaption>
                </figure>
              </div>

              <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">继续浏览</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">
                  这部剧属于「{movie.genres.join(" / ")}」类型，你可以前往对应分类查看更多同题材作品，
                  也可以在全站搜索里按关键词查找同类解析。
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={`/category?genre=${encodeURIComponent(movie.genres[0])}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary-900 px-4 py-2 text-xs font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800 md:text-sm"
                  >
                    更多{movie.genres[0]}剧
                    <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/special"
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700 md:text-sm"
                  >
                    查看相关专题
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700 md:text-sm"
                  >
                    相关影视资讯
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-14 border-t border-background-200 pt-10">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="flex items-center gap-2 font-label text-xs font-bold uppercase tracking-[0.24em] text-accent-700">
                    <span className="h-px w-6 bg-accent-500" />
                    You May Also Like
                  </p>
                  <h2 className="mt-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    相似题材推荐
                  </h2>
                </div>
                <p className="text-xs text-foreground-500">基于类型与标签匹配</p>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                {related.map((item, index) => (
                  <MovieCard key={item.id} movie={item} index={index} />
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}